import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, Bell, Settings as SettingsIcon, User, HelpCircle, Grid, Check, Home, ChevronRight, Ticket } from 'lucide-react';
import ThemeToggle from '../../common/ThemeToggle';
import { GlobalSearch } from '../../common/GlobalSearch';
import { RecentItemsDropdown } from '../../common/RecentItemsDropdown';
import { StandardButton } from '../../standard';
import { useRoles } from '../../../services/queries/roleQueries';
import { persistedSupportTickets } from '../../../services/mockData/supportTickets';
import { apps } from '../../../data/mockData';
import { useAccessibleApps, useIsSystemAdmin } from '../../../services/queries/permissionQueries';
import { shouldBypassRoleChecks } from '../../../config/mvpConfig';
import type { ApplicationType } from '../../../services/permissionService';

// Key for storing read notification IDs in localStorage
const READ_NOTIFICATIONS_KEY = 'crm_read_notifications';

// Helper to get read notification IDs from localStorage
const getReadNotificationIds = (): Set<string> => {
  try {
    const stored = localStorage.getItem(READ_NOTIFICATIONS_KEY);
    if (stored) {
      return new Set(JSON.parse(stored));
    }
  } catch (e) {
    console.error('Error reading notification state:', e);
  }
  return new Set();
};

// Helper to mark a notification as read
const markNotificationAsRead = (notificationKey: string): void => {
  try {
    const readIds = getReadNotificationIds();
    readIds.add(notificationKey);
    localStorage.setItem(READ_NOTIFICATIONS_KEY, JSON.stringify(Array.from(readIds)));
  } catch (e) {
    console.error('Error saving notification state:', e);
  }
};

// Helper to mark all notifications as read
const markAllNotificationsAsRead = (notificationKeys: string[]): void => {
  try {
    const readIds = getReadNotificationIds();
    notificationKeys.forEach(key => readIds.add(key));
    localStorage.setItem(READ_NOTIFICATIONS_KEY, JSON.stringify(Array.from(readIds)));
  } catch (e) {
    console.error('Error saving notification state:', e);
  }
};

interface AdminHeaderProps {
  onToggleSidebar: () => void;
  onNavigate?: (view: string) => void;
  userRole?: string;
  onRoleChange?: (role: string) => void;
}

const getAppType = (appName: string): ApplicationType | 'system_admin' => {
  const lowerName = appName.toLowerCase();
  if (lowerName.includes('system') && lowerName.includes('admin')) return 'system_admin';
  if (lowerName.includes('cybersweep')) return 'cybersweep';
  if (lowerName.includes('credit block')) return 'credit_block';
  if (lowerName.includes('credit management')) return 'credit_management';
  return 'regal_main';
};

const AdminHeader: React.FC<AdminHeaderProps> = ({ onToggleSidebar, onNavigate, userRole, onRoleChange }) => {
  const navigate = useNavigate();
  const [appSwitcherOpen, setAppSwitcherOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [readUpdateTrigger, setReadUpdateTrigger] = useState(0);
  const appSwitcherRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const notificationsRef = useRef<HTMLDivElement>(null);

  const { apps: userAccessibleApps } = useAccessibleApps();
  const { isSystemAdmin } = useIsSystemAdmin();
  const bypassRoles = shouldBypassRoleChecks();

  const accessibleApps = apps.filter(app => {
    const appType = getAppType(app.name);
    if (appType === 'system_admin') return bypassRoles || isSystemAdmin;
    if (bypassRoles) return true;
    return userAccessibleApps.includes(appType);
  });

  // Load roles dynamically
  const { data: roles } = useRoles({ includeSystem: true });

  // PE-764: Support ticket notifications for System Admin
  interface SupportNotification {
    id: string;
    title: string;
    message: string;
    priority: string;
    isRead: boolean;
    submittedBy: string;
  }

  const notifications = useMemo<SupportNotification[]>(() => {
    const allNotifications: SupportNotification[] = [];
    const readIds = getReadNotificationIds();
    const supportTickets = persistedSupportTickets.get();

    // Show all Open or In Progress support tickets to admins
    supportTickets.forEach(ticket => {
      const isActionable = ticket.status === 'Open' || ticket.status === 'In Progress' || ticket.status === 'Pending';

      if (isActionable) {
        const notificationKey = `support_ticket-${ticket.id}`;
        allNotifications.push({
          id: ticket.id,
          title: ticket.title,
          message: `${ticket.category} • ${ticket.status}`,
          priority: ticket.priority,
          isRead: readIds.has(notificationKey),
          submittedBy: ticket.submittedBy
        });
      }
    });

    // Sort: unread first, then by priority
    const priorityOrder: Record<string, number> = {
      'Critical': 0,
      'High': 1,
      'Medium': 2,
      'Low': 3
    };

    return allNotifications.sort((a, b) => {
      if (a.isRead !== b.isRead) {
        return a.isRead ? 1 : -1;
      }
      return (priorityOrder[a.priority] ?? 3) - (priorityOrder[b.priority] ?? 3);
    });
  }, [readUpdateTrigger]);

  const unreadCount = notifications.filter(n => !n.isRead).length;

  const handleNotificationClick = (notification: SupportNotification) => {
    const notificationKey = `support_ticket-${notification.id}`;
    markNotificationAsRead(notificationKey);
    setReadUpdateTrigger(prev => prev + 1);
    setShowNotifications(false);
    if (onNavigate) {
      onNavigate('support-tickets');
    }
  };

  const handleMarkAllAsRead = () => {
    const allKeys = notifications.map(n => `support_ticket-${n.id}`);
    markAllNotificationsAsRead(allKeys);
    setReadUpdateTrigger(prev => prev + 1);
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (appSwitcherRef.current && !appSwitcherRef.current.contains(event.target as Node)) {
        setAppSwitcherOpen(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
      if (notificationsRef.current && !notificationsRef.current.contains(event.target as Node)) {
        setShowNotifications(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleNavigateToApp = (path: string) => {
    setAppSwitcherOpen(false);
    navigate(path);
  };

  return (
    <header className="fixed top-0 left-0 right-0 h-16 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 z-50">
      <div className="flex items-center justify-between h-full px-4">
        {/* Left Section: App Switcher, Menu Toggle & Logo */}
        <div className="flex items-center space-x-4">
          {/* App Switcher Dropdown */}
          <div className="relative" ref={appSwitcherRef}>
            <StandardButton
              variant="ghost"
              onClick={() => setAppSwitcherOpen(!appSwitcherOpen)}
              className="p-2"
              title="Switch application"
            >
              <Grid className="h-5 w-5 text-gray-600 dark:text-gray-300" />
            </StandardButton>

            {/* Dropdown Menu */}
            {appSwitcherOpen && (
              <div className="absolute top-full left-0 mt-2 w-64 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-2 z-50">
                <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700">
                  <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Applications
                  </p>
                </div>

                {/* Product Hub */}
                <button
                  onClick={() => handleNavigateToApp('/')}
                  className="w-full px-4 py-3 flex items-center space-x-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                >
                  <div className="flex-shrink-0 w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                    <Home className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Product Hub</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Main application</p>
                  </div>
                </button>

                {/* Other apps (Credit Management, CyberSweep, Credit Block) */}
                {accessibleApps
                  .filter(app => getAppType(app.name) !== 'system_admin')
                  .map((app, i) => {
                    const path = `/${app.name.toLowerCase().replace(' ', '-')}`;
                    return (
                      <button
                        key={i}
                        onClick={() => handleNavigateToApp(path)}
                        className="w-full px-4 py-3 flex items-center space-x-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                      >
                        <div className={`flex-shrink-0 w-10 h-10 ${app.color} rounded-lg flex items-center justify-center text-white text-lg`}>
                          {app.icon}
                        </div>
                        <div className="flex-1 text-left">
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{app.name}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{app.description}</p>
                        </div>
                      </button>
                    );
                  })}

                {/* System Admin (Current) */}
                <div className="w-full px-4 py-3 flex items-center space-x-3 bg-blue-50 dark:bg-blue-900/20">
                  <div className="flex-shrink-0 w-10 h-10 bg-amber-100 dark:bg-amber-900/30 rounded-lg flex items-center justify-center text-lg">
                    ⚙️
                  </div>
                  <div className="flex-1 text-left">
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">System Admin</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Current location</p>
                  </div>
                  <Check className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                </div>
              </div>
            )}
          </div>

          <StandardButton
            variant="ghost"
            onClick={onToggleSidebar}
            className="p-2"
          >
            <Menu className="h-5 w-5 text-gray-600 dark:text-gray-300" />
          </StandardButton>

          <div className="flex items-center space-x-2">
            <SettingsIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              System Administration
            </h1>
          </div>
        </div>

        {/* Center Section: Global Search */}
        <div className="flex-1 max-w-2xl mx-8 flex justify-center">
          <GlobalSearch onNavigate={onNavigate} userRole={userRole} />
        </div>

        {/* Right Section: Utility Icons */}
        <div className="flex items-center space-x-2">
          <RecentItemsDropdown onNavigate={onNavigate} />

          <StandardButton
            variant="ghost"
            className="p-2"
            title="Help"
          >
            <HelpCircle className="h-5 w-5 text-gray-600 dark:text-gray-300" />
          </StandardButton>

          {/* ThemeToggle hidden until dark mode is fully supported */}

          {/* PE-764: Support Ticket Notifications Dropdown */}
          <div className="relative" ref={notificationsRef}>
            <StandardButton
              variant="ghost"
              className="p-2 relative"
              title="Notifications"
              onClick={() => setShowNotifications(!showNotifications)}
            >
              <Bell className="h-5 w-5 text-gray-600 dark:text-gray-300" />
              {unreadCount > 0 && (
                <span className="absolute top-1 right-1 h-4 w-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-medium">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </StandardButton>

            {showNotifications && (
              <div className="absolute right-0 mt-2 w-96 bg-white dark:bg-gray-800 rounded-lg shadow-xl z-50 border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                    Support Tickets {notifications.length > 0 && `(${notifications.length})`}
                  </h3>
                  {unreadCount > 0 && (
                    <button
                      onClick={handleMarkAllAsRead}
                      className="text-xs text-blue-600 dark:text-blue-400 hover:underline font-medium"
                    >
                      Mark all as read
                    </button>
                  )}
                </div>

                {unreadCount === 0 ? (
                  <div className="px-4 py-8 text-center">
                    <Ticket className="h-8 w-8 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      No unread support tickets
                    </p>
                  </div>
                ) : (
                  <div className="max-h-96 overflow-y-auto">
                    {notifications.filter(n => !n.isRead).slice(0, 10).map((notification) => (
                      <button
                        key={notification.id}
                        onClick={() => handleNotificationClick(notification)}
                        className={`w-full px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 text-left border-b border-gray-100 dark:border-gray-800 last:border-b-0 transition-colors ${
                          notification.isRead ? 'opacity-60' : 'bg-purple-50/50 dark:bg-purple-900/10'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          {/* Unread indicator */}
                          <div className="flex-shrink-0 mt-2">
                            {!notification.isRead ? (
                              <div className="w-2 h-2 bg-purple-500 rounded-full" />
                            ) : (
                              <div className="w-2 h-2" />
                            )}
                          </div>

                          {/* Icon */}
                          <div className={`mt-0.5 p-1.5 rounded-full ${
                            notification.priority === 'Critical'
                              ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
                              : notification.priority === 'High'
                              ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400'
                              : 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400'
                          }`}>
                            <Ticket className="h-3.5 w-3.5" />
                          </div>

                          {/* Content */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-xs uppercase tracking-wider text-gray-400 dark:text-gray-500 font-medium">
                                {notification.id}
                              </span>
                              <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                                notification.priority === 'Critical'
                                  ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                                  : notification.priority === 'High'
                                  ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300'
                                  : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                              }`}>
                                {notification.priority}
                              </span>
                            </div>
                            <p className={`text-sm truncate mt-1 ${
                              notification.isRead
                                ? 'font-normal text-gray-700 dark:text-gray-300'
                                : 'font-medium text-gray-900 dark:text-gray-100'
                            }`}>
                              {notification.title}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                              From: {notification.submittedBy} • {notification.message}
                            </p>
                          </div>

                          {/* Arrow */}
                          <ChevronRight className="h-4 w-4 text-gray-400 mt-1 flex-shrink-0" />
                        </div>
                      </button>
                    ))}
                  </div>
                )}

                {/* Always show View All link to access read notifications */}
                <div className="px-4 py-2 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
                  <button
                    onClick={() => {
                      if (onNavigate) onNavigate('support-tickets');
                      setShowNotifications(false);
                    }}
                    className="text-sm text-blue-600 dark:text-blue-400 hover:underline font-medium"
                  >
                    View all support tickets
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* User Menu Dropdown */}
          <div className="relative" ref={userMenuRef}>
            <StandardButton
              variant="ghost"
              className="p-2"
              title="User Profile"
              onClick={() => setShowUserMenu(!showUserMenu)}
            >
              <User className="h-5 w-5 text-gray-600 dark:text-gray-300" />
            </StandardButton>

            {showUserMenu && (
              <div className="absolute right-0 mt-2 w-52 bg-white dark:bg-gray-800 rounded-md shadow-lg py-1 z-50 border border-gray-200 dark:border-gray-600">
                <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-600">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Role: {userRole || 'employee'}</p>
                </div>
                <button
                  onClick={() => {
                    if (onNavigate) onNavigate('profile');
                    setShowUserMenu(false);
                  }}
                  className="flex items-center w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <User className="h-4 w-4 mr-2" />
                  Your Profile
                </button>
                <button
                  onClick={() => {
                    if (onNavigate) onNavigate('settings');
                    setShowUserMenu(false);
                  }}
                  className="flex items-center w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <SettingsIcon className="h-4 w-4 mr-2" />
                  Settings
                </button>
                <button
                  onClick={() => {
                    if (onNavigate) onNavigate('support-tickets');
                    setShowUserMenu(false);
                  }}
                  className="flex items-center w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <Bell className="h-4 w-4 mr-2" />
                  Support Tickets
                </button>
                <div className="border-t border-gray-200 dark:border-gray-600 my-1"></div>
                {/* Return to Regal Main - Since we're already in System Admin */}
                <button
                  onClick={() => {
                    if (onNavigate) onNavigate('home');
                    setShowUserMenu(false);
                  }}
                  className="flex items-center w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <Home className="h-4 w-4 mr-2" />
                  Return to Regal Main
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default AdminHeader;
